import { ArrowRight, Calendar, Clock, Globe, MapPin } from "lucide-react";

/* Web-optimised variants built from the stage master renders
   (scripts/build_hero_image.py). Sources are ~2000px wide; the 2560/3840
   variants are progressively upscaled + lightly sharpened. */
const HERO_SETS = {
  default: {
    srcSet: [
      "/images/hero-stage4-1280.webp 1280w",
      "/images/hero-stage4-1672.webp 1672w",
      "/images/hero-stage4-2560.webp 2560w",
      "/images/hero-stage4-3840.webp 3840w",
    ].join(", "),
    fallback: "/images/hero-stage4-1672.jpg",
  },
  purple: {
    srcSet: [
      "/images/hero-purple6-1280.webp 1280w",
      "/images/hero-purple6-1672.webp 1672w",
      "/images/hero-purple6-2560.webp 2560w",
      "/images/hero-purple6-3840.webp 3840w",
    ].join(", "),
    fallback: "/images/hero-purple6-1672.jpg",
  },
};

function ctaLabel(mode) {
  if (mode === "tickets_live") return "Get tickets";
  if (mode === "waitlist") return "Join the waitlist";
  return "Join Early Access";
}

/* Accent lands on the final word of the headline. */
function splitHeadline(text) {
  const t = (text || "").trim();
  const i = t.lastIndexOf(" ");
  if (i === -1) return ["", t];
  return [t.slice(0, i + 1), t.slice(i + 1)];
}

/* *word* segments render in the accent colour. */
function renderAccented(text) {
  return (text || "").split("*").map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="accent">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function splitEyebrow(text) {
  const parts = (text || "")
    .split(/[•/|]/)
    .map((p) => p.trim())
    .filter(Boolean);
  return [parts[0] || "", parts.slice(1).join(" / ")];
}

export default function Hero({ settings, variant = "default" }) {
  const heroImg = HERO_SETS[variant] || HERO_SETS.default;
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const s = settings || {};
  const mode = s.mode || "early_access";

  const headLines = (s.hero_headline || "The Next Shift")
    .split("|")
    .map((t) => t.trim())
    .filter(Boolean);
  const [lastStem, lastAccent] = splitHeadline(headLines[headLines.length - 1]);

  const [eyeLead, eyeRest] = splitEyebrow(
    s.hero_eyebrow || "Norwegian AI Summit • 2027"
  );
  const subheadline =
    s.hero_subheadline ||
    "AI is changing how we build, lead and compete.";

  const meta = [
    {
      key: "date",
      label: "Date",
      icon: Calendar,
      value: s.hero_meta_date_value || "13 April 2027",
    },
    {
      key: "duration",
      label: "Duration",
      icon: Clock,
      value: s.hero_meta_duration_value || "One full day",
    },
    {
      key: "venue",
      label: "Venue",
      icon: MapPin,
      value: s.hero_meta_venue_value || "Grieghallen",
    },
    {
      key: "location",
      label: "Audience",
      icon: Globe,
      value: s.hero_meta_audience_value || "~700 in the room",
    },
  ];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="section-dark relative w-full overflow-hidden bg-[var(--dark)] text-[var(--paper)] hero-shell"
    >
      {/* Full-bleed background — stage right, black void left for the copy. */}
      <picture>
        <source type="image/webp" srcSet={heroImg.srcSet} sizes="100vw" />
        <img
          src={heroImg.fallback}
          alt="Norwegian AI Summit — a keynote speaker on the main stage at Grieghallen"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-[45%_center] md:object-center img-editorial hero-img-in hero-img-mac"
          data-testid="hero-image"
        />
      </picture>

      {/* Mobile scrim — vertical, keeps the photograph readable behind the copy */}
      <div
        className="absolute inset-0 md:hidden"
        style={{
          background:
            "linear-gradient(to bottom, rgba(4,5,7,0.82) 0%, rgba(4,5,7,0.6) 42%, rgba(4,5,7,0.45) 66%, rgba(4,5,7,0.85) 100%)",
        }}
        aria-hidden
      />

      {/* Left gradient — deep black from the left, transparent toward the stage */}
      <div
        className="absolute inset-0 hidden md:block"
        style={{
          background:
            "linear-gradient(90deg, rgba(3,4,6,0.82) 0%, rgba(3,4,6,0.6) 24%, rgba(3,4,6,0.3) 46%, rgba(3,4,6,0.05) 68%, rgba(3,4,6,0) 100%)",
        }}
        aria-hidden
      />

      {/* Bottom vignette — carries the info rail */}
      <div
        className="absolute inset-x-0 bottom-0 h-[48%] pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(4,5,7,0.9) 0%, rgba(4,5,7,0.5) 36%, rgba(4,5,7,0) 100%)",
        }}
        aria-hidden
      />

      {/* Top scrim — keeps the fixed nav legible */}
      <div
        className="absolute inset-x-0 top-0 h-[170px] pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(4,5,7,0.72) 0%, rgba(4,5,7,0.38) 52%, rgba(4,5,7,0) 100%)",
        }}
        aria-hidden
      />

      {/* Film grain — subtle texture that sharpens the dark surfaces */}
      <div className="absolute inset-0 hero-grain pointer-events-none" aria-hidden />

      {/* ---------------- Content ---------------- */}
      <div className="relative z-10 hero-wide flex flex-col hero-shell hero-pad">
        {/* Top band — date / venue kicker */}
        {/* Message block — brand line + theme, left-anchored like OBF */}
        <div className="grid grid-cols-12 flex-1 items-center hero-mid">
          <div className="col-span-12 md:col-span-11 lg:col-span-9 lg:col-start-2 xl:col-span-8 xl:col-start-2 hero-stack">
            <div className="flex flex-col gap-5 md:gap-6">
              <div className="rise rise-1" data-testid="hero-eyebrow">
                <span className="hero-kicker inline-flex items-center">
                  <span>{eyeLead}</span>
                  {eyeRest && (
                    <span className="accent" style={{ marginLeft: "0.55em" }}>
                      {eyeRest}
                    </span>
                  )}
                </span>
              </div>

              <h1 className="hero-display rise rise-2" data-testid="hero-title">
                {headLines.slice(0, -1).map((ln, i) => (
                  <span key={i} className="block">
                    {ln}
                  </span>
                ))}
                <span className="block">
                  {lastStem}
                  {lastAccent && <span className="stop">{lastAccent}</span>}
                </span>
              </h1>
            </div>

            <p className="hero-sub max-w-[42ch] rise rise-3" data-testid="hero-positioning">
              {renderAccented(subheadline)}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-10 rise rise-4">
              <a
                href="#early-access"
                onClick={scrollTo("early-access")}
                className="btn-accent btn-accent--lg justify-center sm:justify-start"
                data-testid="hero-cta-early-access"
              >
                {ctaLabel(mode)}
                <ArrowRight size={16} strokeWidth={2} className="arrow" />
              </a>
              <a
                href="#program"
                onClick={scrollTo("program")}
                className="hero-link w-fit"
                data-testid="hero-cta-program"
              >
                Explore the summit
                <ArrowRight size={15} strokeWidth={2} className="arrow" />
              </a>
            </div>
          </div>
        </div>

        {/* ---------------- Info rail: metadata ---------------- */}
        <div className="info-rail hero-rail-gap rise rise-5" data-testid="hero-info-rail">
          {/* Metadata — four columns with accent icons, hairline dividers */}
          <div
            className="info-rail__row grid grid-cols-2 md:grid-cols-4 gap-y-7 gap-x-6"
            data-testid="hero-info-block"
          >
            {meta.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.key}
                  className={`flex flex-col gap-2.5 ${
                    i > 0
                      ? "md:border-l md:border-[var(--panel-border)] md:pl-7 lg:pl-9"
                      : ""
                  }`}
                  data-testid={`hero-meta-${item.key}`}
                >
                  <span className="flex items-center gap-2.5">
                    <Icon size={13} strokeWidth={1.75} className="meta-ico" aria-hidden />
                    <span className="meta-label">{item.label}</span>
                  </span>
                  <p className="meta-value">{item.value}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
